import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import './SubscriptionPage.css';

interface Price {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  interval: string;
  user_type: string;
}

// Free tier definition (not a paid Stripe product)
const FREE_TIER: Price = {
  id: 'free',
  name: 'Free',
  description: 'Get started with basic features',
  amount: 0,
  currency: 'usd',
  interval: 'month',
  user_type: 'free',
};

const SubscriptionPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prices, setPrices] = useState<Price[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingPriceId, setProcessingPriceId] = useState<string | null>(null);
  const [currentUserType, setCurrentUserType] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [tierLimits, setTierLimits] = useState<{ free: number; standard: number; premium: number } | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<boolean>(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<string | null>(null);
  const [reactivating, setReactivating] = useState<boolean>(false);
  const [pendingDowngradeTier, setPendingDowngradeTier] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch tier limits
        const tierLimitsResponse = await apiService.getTierLimits();
        if (!tierLimitsResponse || !tierLimitsResponse.data) {
          setError('Failed to load tier limits. Please refresh the page.');
          setLoading(false);
          return;
        }
        setTierLimits(tierLimitsResponse.data);

        // Fetch prices
        const pricesResponse = await apiService.listPrices();
        // Include free tier at the beginning, then paid tiers
        setPrices([FREE_TIER, ...pricesResponse.data.prices]);

        // Fetch user limits to get current user_type
        const limitsResponse = await apiService.getUserLimits(user.sub);
        setCurrentUserType(limitsResponse.data.user_type);
        setCancelAtPeriodEnd(limitsResponse.data.cancel_at_period_end);
        setSubscriptionEndDate(limitsResponse.data.subscription_end_date);
        setPendingDowngradeTier(limitsResponse.data.pending_downgrade_tier || null);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load pricing plans');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate]);

  const handleSubscribe = async (priceId: string) => {
    if (!user || !tierLimits) return;

    setProcessingPriceId(priceId);
    setError(null);
    setSuccessMessage(null);

    try {
      // Get current user limits to check if downgrade will result in data loss
      const limitsResponse = await apiService.getUserLimits(user.sub);
      const currentChunks = limitsResponse.data.current_chunks;

      // Determine chunk limit for the target plan using backend limits
      let targetLimit = 0;
      if (priceId === 'free') {
        targetLimit = tierLimits.free;
      } else {
        const targetPrice = prices.find(p => p.id === priceId);
        if (targetPrice?.user_type === 'standard') {
          targetLimit = tierLimits.standard;
        } else if (targetPrice?.user_type === 'premium') {
          targetLimit = tierLimits.premium;
        }
      }

      // Edge case: If targetLimit is 0 and priceId is not 'free', it's an invalid plan selection
      if (targetLimit === 0 && priceId !== 'free') {
        setError('Invalid subscription plan selected');
        setProcessingPriceId(null);
        return;
      }

      // Check if downgrade will cause data loss
      const isDowngrade = currentChunks > targetLimit;

      if (isDowngrade) {
        const chunksToDelete = currentChunks - targetLimit;
        const confirmed = window.confirm(
          `⚠️ WARNING: You currently have ${currentChunks} chunks, but the plan you're selecting only allows ${targetLimit} chunks.\n\n` +
          `If you proceed, your ${chunksToDelete} OLDEST chunks will be PERMANENTLY DELETED when your current subscription period ends.\n\n` +
          `This action CANNOT be undone.\n\n` +
          `Do you want to continue?`
        );

        if (!confirmed) {
          setProcessingPriceId(null);
          return;
        }
      }

      // Handle free tier selection by canceling current subscription
      if (priceId === 'free') {
        await apiService.cancelSubscription(user.sub);
        // Refresh user data
        const updatedLimitsResponse = await apiService.getUserLimits(user.sub);
        setCurrentUserType(updatedLimitsResponse.data.user_type);
        setCancelAtPeriodEnd(updatedLimitsResponse.data.cancel_at_period_end);
        setSubscriptionEndDate(updatedLimitsResponse.data.subscription_end_date);
        setPendingDowngradeTier(updatedLimitsResponse.data.pending_downgrade_tier || null);
        if (isDowngrade) {
          setSuccessMessage('Successfully scheduled downgrade to Free plan. Your oldest chunks will be deleted and your subscription will be canceled at the end of the billing period.');
        } else {
          setSuccessMessage('Successfully scheduled downgrade to Free plan. Your subscription will be canceled at the end of the billing period.');
        }
        setProcessingPriceId(null);
        return;
      }

      // If user is already on a paid tier (standard or premium), update existing subscription
      // Otherwise, create a new checkout session
      if (currentUserType === 'standard' || currentUserType === 'premium') {
        const response = await apiService.updateSubscription(user.sub, priceId);
        // Refresh user data
        const updatedLimitsResponse = await apiService.getUserLimits(user.sub);
        setCurrentUserType(updatedLimitsResponse.data.user_type);
        setCancelAtPeriodEnd(updatedLimitsResponse.data.cancel_at_period_end);
        setSubscriptionEndDate(updatedLimitsResponse.data.subscription_end_date);
        setPendingDowngradeTier(updatedLimitsResponse.data.pending_downgrade_tier || null);
        if (isDowngrade) {
          setSuccessMessage(response.data.message || 'Subscription downgrade scheduled! Your oldest chunks will be deleted at the end of your billing period.');
        } else {
          setSuccessMessage(response.data.message || 'Subscription upgraded successfully!');
        }
        setProcessingPriceId(null);
      } else {
        // Free tier user subscribing for the first time
        const response = await apiService.createCheckoutSession(priceId, user.sub);
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to process request');
      setProcessingPriceId(null);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    const price = (amount / 100).toFixed(2);
    const currencySymbol = currency === 'usd' ? '$' : currency.toUpperCase();
    return { price, currencySymbol };
  };

  const isCurrentPlan = (userType: string) => {
    return currentUserType === userType;
  };

  const handleReactivate = async () => {
    if (!user) return;

    setReactivating(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await apiService.reactivateSubscription(user.sub);
      // Refresh user data
      const updatedLimitsResponse = await apiService.getUserLimits(user.sub);
      setCurrentUserType(updatedLimitsResponse.data.user_type);
      setCancelAtPeriodEnd(updatedLimitsResponse.data.cancel_at_period_end);
      setSubscriptionEndDate(updatedLimitsResponse.data.subscription_end_date);
      setPendingDowngradeTier(updatedLimitsResponse.data.pending_downgrade_tier || null);
      setSuccessMessage('Your subscription has been reactivated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setReactivating(false);
    }
  };

  // Determine button text based on current state
  const getButtonText = (price: Price) => {
    const isCurrent = isCurrentPlan(price.user_type);

    if (processingPriceId === price.id) {
      return 'Processing...';
    }

    // Check if this is the pending downgrade target
    if (pendingDowngradeTier && price.user_type === pendingDowngradeTier) {
      return 'Scheduled';
    }

    if (isCurrent) {
      // If there's a pending downgrade to a different tier, don't show "Cancellation Scheduled"
      // because the user is downgrading, not canceling
      if (cancelAtPeriodEnd && !pendingDowngradeTier) {
        // User has a pending cancellation to Free tier
        return 'Cancellation Scheduled';
      }
      return 'Current Plan';
    }

    // Not current plan
    if (price.user_type === 'free') {
      return 'Select Free Plan';
    }

    // For paid plans, check if user is on a paid plan or free
    if (currentUserType === 'standard' || currentUserType === 'premium') {
      // User already has a paid subscription
      const currentIndex = ['free', 'standard', 'premium'].indexOf(currentUserType);
      const targetIndex = ['free', 'standard', 'premium'].indexOf(price.user_type);

      if (targetIndex > currentIndex) {
        return 'Upgrade';
      } else {
        return 'Downgrade';
      }
    }

    return 'Subscribe';
  };

  // Determine if button should be disabled
  const isButtonDisabled = (price: Price) => {
    const isCurrent = isCurrentPlan(price.user_type);

    if (processingPriceId === price.id) {
      return true;
    }

    // If this is the pending downgrade target, disable the button
    if (pendingDowngradeTier && price.user_type === pendingDowngradeTier) {
      return true;
    }

    if (isCurrent && !cancelAtPeriodEnd) {
      // Current plan without pending cancellation - disable
      return true;
    }

    // If there's a pending downgrade, disable the current plan button
    // (user should only see "Keep Current Plan" link)
    if (isCurrent && pendingDowngradeTier) {
      return true;
    }

    // If there's a pending cancellation, the button should be enabled for other plans
    return false;
  };

  if (loading) {
    return <div className="loading-message">Loading subscription plans...</div>;
  }

  return (
    <div className="subscription-page">
      <div className="subscription-header">
        <h1>Choose Your Plan</h1>
        <p>Unlock more features and storage with a subscription</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="modal-overlay" onClick={() => setSuccessMessage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Success!</h3>
            <p>{successMessage}</p>
            <button
              className="modal-close-button"
              onClick={() => setSuccessMessage(null)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div className="pricing-cards">
        {prices.sort((a, b) => a.amount - b.amount).map((price) => {
          const { price: formattedPrice, currencySymbol } = formatPrice(price.amount, price.currency);
          const isFeatured = price.user_type === 'premium';
          const isCurrent = isCurrentPlan(price.user_type);

          return (
            <div
              key={price.id}
              className={`pricing-card ${isFeatured ? 'featured' : ''}`}
            >
              {isFeatured && <div className="featured-badge">Most Popular</div>}

              <h2>{price.name}</h2>

              <div className="price">
                <span className="price-currency">{currencySymbol}</span>
                {formattedPrice}
                <span className="price-interval">/{price.interval}</span>
              </div>

              {/* <p className="description">{price.description}</p> */}

              <ul className="features-list">
                {price.user_type === 'free' && (
                  <>
                    {tierLimits ? (
                      <>
                        <li>{tierLimits.free.toLocaleString()} chunks storage</li>
                        <li>Upload feature</li>
                        <li>Search feature</li>
                        <li>Export feature</li>
                      </>
                    ) : (
                      <li>Loading plan details...</li>
                    )}
                  </>
                )}
                {price.user_type === 'standard' && (
                  <>
                    {tierLimits ? (
                      <>
                        <li>Everything in Free, plus...</li>
                        <li>{tierLimits.standard.toLocaleString()} chunks storage</li>
                      </>
                    ) : (
                      <li>Loading plan details...</li>
                    )}
                  </>
                )}
                {price.user_type === 'premium' && (
                  <>
                    {tierLimits ? (
                      <>
                        <li>Everything in Standard, plus...</li>
                        <li>{tierLimits.premium.toLocaleString()} chunks storage</li>
                      </>
                    ) : (
                      <li>Loading plan details...</li>
                    )}
                  </>
                )}
              </ul>

              <button
                className={`subscribe-button ${isCurrent && cancelAtPeriodEnd && !pendingDowngradeTier ? 'cancellation-scheduled' : ''}`}
                onClick={() => handleSubscribe(price.id)}
                disabled={isButtonDisabled(price)}
              >
                {getButtonText(price)}
              </button>

              {isCurrent && (cancelAtPeriodEnd || pendingDowngradeTier) && (
                <button
                  className="keep-plan-link"
                  onClick={handleReactivate}
                  disabled={reactivating}
                >
                  {reactivating ? 'Reactivating...' : 'Keep Current Plan'}
                </button>
              )}

              {isCurrent && (cancelAtPeriodEnd || pendingDowngradeTier) && subscriptionEndDate && (
                <p className="cancellation-notice">
                  {pendingDowngradeTier
                    ? `Downgrading to ${pendingDowngradeTier.charAt(0).toUpperCase() + pendingDowngradeTier.slice(1)} on ${new Date(subscriptionEndDate).toLocaleDateString()}`
                    : `Access until ${new Date(subscriptionEndDate).toLocaleDateString()}`
                  }
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SubscriptionPage;
